import ProspectingClientEditor from "./components/ProspectingClientEditor";
import { connectClients } from "./services/liveClients";
import {startCommercialSession,advanceCommercialSession} from './services/prospecting-session.mjs';
import CorrectLastCall, { applyCallCorrectionResponse } from "../components/CorrectLastCall";
import { applyInactivityRules } from "./services/crm-v2-policy.mjs";
import CrmV2Panel from "../components/CrmV2Panel";
import CommercialFavorites from "../components/CommercialFavorites";
import { filterProspectingClients, sentenceCase, normalizeCommercialSearch, prospectingFilters, normalizeProspectingFilter } from "./services/prospecting-filter.mjs";
import FranceTravailRobot from "../components/FranceTravailRobot";
import franceTravailApi from "../axios/AxiosInstance";
import { mergeFranceTravailProspects, watchFranceTravailProspects } from "./services/franceTravail.mjs";
import NeedQuotationBuilder from "./components/NeedQuotationBuilder";
import { recordQuotationForNeed } from "./services/recordedQuotation";
import { createPortal } from "react-dom";
import { saveFinancialGoal } from './services/profitability.mjs';
import { setWeeklyGoalPlan } from "./services/management.mjs";
import { saveUsualWeek } from "./services/work-hours.mjs";
import React, { useEffect, useState } from "react";
import {
  Building2, BriefcaseBusiness, ChevronRight,
  Users,
  Search,
  Upload,
  Play,
  Pause,
  CalendarDays,
  FileText,
  RefreshCcw,
  BarChart3,
  PhoneCall,
  X,
  ThumbsUp,
  TrendingDown,
  UserRound,
  Mail,
} from "lucide-react";
import Dashboard from "./Dashboard.jsx";
import ClientRecord from "./components/ClientRecord";
import {
  Panel,
  Field,
  Select,
  TextArea,
  Form,
  Modal,
  Empty,
  K,
} from "./components/ui";
import {
  MotifSelect,
  ContactForm,
  CallResultForm,
  NeedFields,
  TaskForm,
} from "./components/CommercialForms";
import ImportProspects from "./pages/ImportProspects";
import Needs from "./pages/Needs";
import Manager from "./pages/Manager";
import Reactivation from "./pages/Reactivation";
import Agenda from "./pages/Agenda";
import { loadState, saveState, storageKey } from "./services/repository.mjs";
import * as crm from "./services/commercial.mjs";
import {
  getNextActions,
  getNextBestAction,
  userDailyState,
  getDailyGoals,
  setDailyGoals,
  periodIndicators,
} from "./services/daily.mjs";
import { goalLabels } from "./components/daily/DailyWidgets";
import {
  formatDate,
  todayParis,
  addBusinessDays,
  dateValue,
} from "./utils/calendar.mjs";
import "./style.css";
const icons = [
  ThumbsUp,
  CalendarDays,
  Pause,
  TrendingDown,
  Users,
  PhoneCall,
  TrendingDown,
  X,
];
function CallChoices({ disabled = false, onResult }) {
  const [query,setQuery]=useState("");
  return <div className="space-y-4" onKeyDown={event=>{if(event.altKey&&/^[1-9]$/.test(event.key)){const options=crm.resultOptions.filter(label=>normalizeCommercialSearch(label).includes(normalizeCommercialSearch(query)));const label=options[Number(event.key)-1];if(label&&!disabled){event.preventDefault();onResult(label);}}}}><input aria-label="Rechercher un résultat d’appel" placeholder="Rechercher un résultat…" value={query} onChange={event=>setQuery(event.target.value)} className="w-full rounded-lg border bg-white p-2"/>{Object.entries(crm.resultGroups).map(([category,options])=><section key={category}><h3 className="mb-2 font-semibold">{category}</h3><div className="resultGrid">{options.filter(label=>normalizeCommercialSearch(label).includes(normalizeCommercialSearch(query))).map(label=><button key={label} disabled={disabled} onClick={()=>onResult(label)}><PhoneCall aria-hidden="true"/>{label}</button>)}</div></section>)}</div>;
}

export function App({
  initialData,
  headerActionsTarget,
  dayContent,
  onTabChange,
  persistenceKey = storageKey,
  onPersist,
  onRefresh,
  currentUser = {
    id: "commercial-demo",
    name: "Mon espace",
    role: "commercial",
    agency: "Toulouse",
  },
}) {
  const isHyperadmin = currentUser?.role === "hyperadmin";
  const canPilot =
    isHyperadmin || ["manager", "n+1"].includes(currentUser?.role);
  const user = { ...crm.currentCommercial, ...currentUser };
  const [state, setState] = useState(() => initialData || loadState());
  useEffect(() => watchFranceTravailProspects(franceTravailApi, rows => {
    setState(current => mergeFranceTravailProspects(current, rows));
  }), [persistenceKey]);
  const [tab, setTab] = useState(() =>
    state.pendingResultClientId ? "prospects" : (["day","prospects","reactivation","needs","agenda"].includes(state.commercialView?.tab)?state.commercialView.tab:"day"),
  );
  useEffect(() => { onTabChange?.(tab); }, [tab, onTabChange]);
  const [selectedId, setSelectedId] = useState(
    () => state.pendingResultClientId || state.commercialView?.selectedId || state.clients[0]?.id,
  );
  const [clientOpen, setClientOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [search, setSearch] = useState(state.commercialView?.search||"");
  const [filter, setFilter] = useState(state.commercialView?.filter||"all");
  const [onlyMine, setOnlyMine] = useState(!!state.commercialView?.onlyMine);
  const [note, setNote] = useState("");
  const [toast, setToast] = useState("");
  const [actionFeedback, setActionFeedback] = useState(null);
  const [session, setSession] = useState(()=>state.prospectingSession || null);
  const [paused, setPaused] = useState(()=>!!state.prospectingSession?.paused);
  useEffect(()=>{setState(current=>({...current,prospectingSession:session?{...session,paused}:null}));},[session,paused]);
  useEffect(()=>{setState(current=>({...current,commercialView:{tab,selectedId,search,filter,onlyMine}}));},[tab,selectedId,search,filter,onlyMine]);
  const [savingResult, setSavingResult] = useState(false);
  const [needFocus, setNeedFocus] = useState(null);
  const [storageError, setStorageError] = useState(false);
  const today = todayParis();
  const visibleState = { ...state, engagements: [], tasks: state.tasks.filter(task => task.category !== "engagement" && !task.engagementId) };
  const myState = userDailyState(visibleState, user.id);
  const myGoals = getDailyGoals(state, user.id);
  const myActual = periodIndicators(myState, today, today);
  const recommended = getNextBestAction(
    myState,
    today,
    actionFeedback?.clientId,
  );
  useEffect(() => {
    setState((s) => applyInactivityRules(crm.rolloverTasks(s, today),today));
  }, [today]);
  useEffect(() => {
    if (onPersist) {
      let active = true;
      onPersist(state).then(
        () => { if (active) setStorageError(false); },
        () => { if (active) setStorageError(true); },
      );
      return () => { active = false; };
    }
    try {
      if (initialData) localStorage.setItem(persistenceKey, JSON.stringify(state));
      else saveState(state);
      setStorageError(false);
    } catch {
      setStorageError(true);
    }
  }, [state, initialData, persistenceKey, onPersist]);
  useEffect(() => {
    const listener = (e) => {
      if (e.key === persistenceKey && e.newValue) {
        try {
          const next = crm.normalizeState(JSON.parse(e.newValue));
          setState(next);
          if (next.pendingResultClientId) {
            setSelectedId(next.pendingResultClientId);
            setTab("prospects");
          }
          setToast("Données actualisées depuis un autre onglet.");
        } catch {}
      }
    };
    window.addEventListener("storage", listener);
    return () => window.removeEventListener("storage", listener);
  }, [persistenceKey]);
  const openTasks = visibleState.tasks.filter(
    (t) => !t.done && (canPilot || t.category !== "manager"),
  );
  const dueTasks = openTasks.filter((t) => t.due_date_original <= today);
  const todayResults = state.results.filter(
    (r) => todayParis(new Date(r.createdAt)) === today,
  );
  const searched = crm.searchEstablishments(state.clients, search);
  const visibleClients = filterProspectingClients(searched,{filter:filter==="favorites"?"all":filter,onlyMine,userId:user.id,today}).filter(c=>filter!=="favorites"||(state.commercialFavorites||[]).includes(String(c.client_id||c.id)));
  const p = state.clients.find(c=>c.id===state.pendingResultClientId) || (tab === "prospects" ? visibleClients.find(c=>c.id===selectedId) || visibleClients[0] : null) || state.clients.find(c=>c.id===selectedId) || state.clients[0];
  const contact = p.contacts.find(c=>c.active && c.primary);
  const hasVisibleSelection = visibleClients.some(c=>c.id===p.id) || Boolean(state.pendingResultClientId);
  const nav = [
    ["day", "Ma journée", BriefcaseBusiness],
    ["needs", "Besoin/devis", FileText],
    ["react", "Réactivation", RefreshCcw],
    ["prospects", "Prospection", Users],
    ["follow", "Agenda", CalendarDays],
    ...(canPilot ? [["manager", "Pilotage N+1", BarChart3]] : []),
  ];
  const navCounts = {
    needs: state.needs.length,
    react: crm.reactivationClients(visibleState, today).length,
    prospects: visibleClients.length,
  };
  function applyNext(next) {
    const result =
      next.results.length > state.results.length ? next.results.at(-1) : null;
    const need = next.needs.find(
      (n) => !state.needs.some((old) => old.id === n.id),
    );
    const done = next.tasks.find(
      (t) => t.done && !state.tasks.find((old) => old.id === t.id)?.done,
    );
    const quote = next.quotes.find(
      (q) => q.sentAt && !state.quotes.find((old) => old.id === q.id)?.sentAt,
    );
    if (!next.pendingResultClientId && (result || need || done || quote))
      setActionFeedback({
        label: need
          ? "🔥 Nouveau besoin détecté"
          : result
            ? "✓ Résultat enregistré"
            : "✓ Action enregistrée",
        clientId: (result || need || done || quote).clientId,
      });
    setState(next);
    return next;
  }
  function commit(fn) {
    return applyNext(crm.recalculateScores(fn(state)));
  }
  function run(fn, message) {
    try {
      commit(fn);
      if (message) setToast(message);
      return true;
    } catch (e) {
      setToast(e.message);
      return false;
    }
  }
  function navigate(next) {
    if (state.pendingResultClientId && next !== "prospects") {
      setToast(
        "Renseigne le résultat final du même appel avant de quitter la prospection.",
      );
      return false;
    }
    if (next === "manager" && !canPilot) return false;
    setTab(next);
    if (next === "needs") setNeedFocus(null);
    return true;
  }
  function selectClient(id) {
    if (state.pendingResultClientId && id !== state.pendingResultClientId) {
      setToast(
        "Termine le résultat final de l’appel en cours avant de changer d’établissement.",
      );
      return false;
    }
    if (!state.clients.some((c) => c.id === id)) return false;
    setSelectedId(id);
    setNote("");
    return true;
  }
  function openCall(id) {
    if (!selectClient(id)) return;
    const c = state.clients.find((c) => c.id === id);
    if (!run((s) => crm.claimCommercialWork(s, id, user))) return;
    setTab("prospects");
    setPaused(false);
    setToast(
      c.ownerId !== user.id
        ? `Responsable de cet établissement : ${c.owner} (${c.agency}). Les opérations restent attribuées à son responsable.`
        : "",
    );
  }
  function callPurpose(client) {
    const task = state.tasks
      .filter(t => t.clientId === client.id && !t.done && t.category !== "manager")
      .sort((a, b) => a.due_date_original.localeCompare(b.due_date_original))[0];
    if (task?.quoteId || task?.category === "quote") return "Relance devis";
    if (task?.engagementId || task?.category === "engagement") return "Engagement client";
    if (task?.category === "appointment") return "Rendez-vous";
    if (task?.category === "contact") return "Actualisation du contact";
    if (/plaquette/i.test(task?.title || "")) return "Relance plaquette";
    if (/rappel|rappeler/i.test(task?.title || "")) return "Rappel";
    if (crm.reactivationSituation(client, today)) return "Réactivation";
    if (state.needs.some(n => n.clientId === client.id && !["perdu", "suspendu", "signé"].includes(n.status))) return "Suivi du besoin";
    return "Prospection";
  }
  function startSession() {
    const actions = getNextActions(
      userDailyState(state, user.id),
      today,
    ).filter((a) => visibleClients.some((c) => c.id === a.client.id));
    const ids = actions.map((a) => a.client.id);
    if (state.pendingResultClientId) {
      const index = ids.indexOf(state.pendingResultClientId);
      if (index >= 0) ids.splice(index, 1);
      ids.unshift(state.pendingResultClientId);
    }
    setSession(startCommercialSession(ids));
    setPaused(false);
    setTab("prospects");
    if (state.pendingResultClientId) openCall(state.pendingResultClientId);
    else if (actions.length) {
      setModal(null);
      setActionFeedback(null);
      openCall(actions[0].client.id);
    }
    else
      setToast(
        "La file du jour est terminée. Les futurs suivis restent programmés.",
      );
  }
  function nextProspect() {
    const next = getNextBestAction(
      userDailyState(state, user.id),
      today,
      modal?.clientId || p.id,
    );
    setModal(null);
    setActionFeedback(null);
    if (next) openCall(next.client.id);
    else {
      setSession(null);
      setTab("day");
      setToast("File terminée : tous les résultats ont été enregistrés.");
    }
  }
  function openRecommended(action) {
    if (!action) return;
    setModal(null);
    setActionFeedback(null);
    if (action.task) openTask(action.task);
    else {
      openCall(action.client.id);
      setModal({ type: "result-picker" });
    }
  }
  function chooseResult(label) {
    if (paused) return;
    setModal({ type: "result", label, clientId: p.id, operationId:crypto.randomUUID() });
  }
  function saveResult(data) {
    if (savingResult) return;
    let next = crm.recalculateScores(
      crm.recordResult(state, p.id, modal.label, {...data,operationId:modal.operationId}),
    );
    if(session&&!paused){const progress=advanceCommercialSession(session,p.id,next.clients.filter(c=>!crm.isBlockedForProspecting(c)).map(c=>c.id));next={...next,prospectingSession:progress.session,commercialView:{...next.commercialView,selectedId:progress.nextId,tab:progress.nextId?'prospects':'day'}};}
    setSavingResult(true);
    (onPersist ? onPersist(next, 0) : Promise.resolve())
      .then(async () => {
        // Refresh only after the call was saved; a failed refresh must never
        // report a failed save or ask the user to record the same call twice.
        let refreshFailed = false;
        if (onRefresh) {
          try { next = crm.recalculateScores(await onRefresh()); }
          catch { refreshFailed = true; }
        }
        applyNext(next);
        setStorageError(false);
        setSession((s) =>
          s ? { ...s, ids: [...new Set([...s.ids, p.id])], completed: [...new Set([...s.completed, p.id])] } : s,
        );
        if (session && !paused) {
          const completed=new Set([...session.completed,p.id]);
          const following=getNextActions(userDailyState(next,user.id),today).find(action=>session.ids.includes(action.client.id)&&!completed.has(action.client.id));
          if(following) {
            applyNext(crm.claimCommercialWork(next,following.client.id,user));
            setSelectedId(following.client.id);setTab("prospects");setModal(null);
            setToast("Résultat enregistré. Prospect suivant : "+following.client.name);
          } else {setSession(null);setTab("day");setModal(null);setToast("Session terminée : les résultats sont enregistrés.");}
        } else setModal({
          type: "saved",
          label: modal.label,
          clientId: p.id,
          due: data.due,
        });
        setNote("");
        if (refreshFailed) setToast("Résultat enregistré. La liste locale est à jour ; la synchronisation serveur sera retentée à la prochaine validation.");
      })
      .catch(() => {
        setStorageError(true);
        setToast(
          "Le résultat n’a pas été enregistré sur le serveur. Réessaie avant de quitter cet appel.",
        );
      })
      .finally(() => setSavingResult(false));
  }
  function openTask(task) {
    if (!task) return;
    if (task.category === "manager") {
      if (canPilot) setModal({ type: "task-detail", task });
      return;
    }
    if (!selectClient(task.clientId)) return;
    if (task.category === "invoice") {
      setModal({ type: "invoice", task, clientId: task.clientId });
      return;
    }
    if (task.category === "quote" || task.quoteId) {
      navigate("needs");
      setNeedFocus(task.needId || null);
      return;
    }
    if (task.category === "contact") {
      setTab("prospects");
      setModal(
        task.contactId
          ? {
              type: "contact-form",
              contact: state.clients
                .find((c) => c.id === task.clientId)
                ?.contacts.find((c) => c.id === task.contactId),
              contactId: task.contactId,
              clientId: task.clientId,
            }
          : { type: "contact-choice", clientId: task.clientId },
      );
      return;
    }
    if (["email", "appointment", "engagement"].includes(task.category)) {
      setModal({ type: "task-detail", task });
      return;
    }
    openCall(task.clientId);
    setModal({ type: "result-picker" });
  }
  function showNeed(need) {
    if (!need) return;
    if (navigate("needs")) setNeedFocus(need.id);
  }
  const recordProps = (c) => ({
    state,
    client: c,
    onEditClient: /^[a-f0-9]{24}$/i.test(String(c.client_id || c.establishment_id || c.id)) ? () => setModal({type:"client-edit",clientId:c.id}) : undefined,
    onContact: (options) =>
      setModal({
        type: "contact-form",
        clientId: c.id,
        duringCall: tab === "prospects",
        ...options,
      }),
    onEdit: (contact) =>
      setModal({
        type: "contact-form",
        clientId: c.id,
        contact,
        contactId: contact.id,
      }),
    onQuote: showNeed,
    onNeed: () => setModal({ type: "need", clientId: c.id }),
    onCorrectLastCall: (canPilot || String(c.ownerId)===String(user.id)) ? ()=>setModal({type:"call-correction",clientId:c.id}) : undefined,
    onOwner: isHyperadmin
      ? () => setModal({ type: "owner", clientId: c.id })
      : undefined,
  });
  function close() {
    setModal(null);
  }
  const headerActions = (
(tab === "day" || tab === "prospects") && (
            <div className="headerActions">
              <FranceTravailRobot api={franceTravailApi} onSync={rows => setState(current => mergeFranceTravailProspects(current, rows))} />
              {tab === "day" && (
                <button
                  className="secondary"
                  disabled={!!state.pendingResultClientId}
                  onClick={() => setModal({ type: "import" })}
                >
                  <Upload />
                  Importer prospects
                </button>
              )}
              {tab === "day" && (
                <button className="primary" onClick={startSession}>
                  <Play />
                  Commencer ma prospection
                </button>
              )}
              {session && tab === "prospects" && (
                <button
                  className="secondary"
                  disabled={!!state.pendingResultClientId}
                  onClick={() => setPaused((v) => !v)}
                >
                  {paused ? <Play /> : <Pause />}
                  {paused ? "Reprendre" : "Mettre en pause"}
                </button>
              )}
            </div>
          )
  );
  const modalClient = state.clients.find((c) => c.id === modal?.clientId) || p;
  const renderAssistant = (view, activityContent) => (<CrmV2Panel view={view} activityContent={activityContent} onFavorites={ids=>setState(current=>({...current,commercialFavorites:ids}))} onSelectFavorite={id=>openCall(id)} onClientSettings={(id,settings)=>setState(current=>({...current,clients:current.clients.map(c=>c.id===id?{...c,inactivityThresholds:settings}:c)}))} state={canPilot ? visibleState : myState} client={p} api={franceTravailApi} canConfigure={canPilot} onRules={data=>setState(current=>({...current,automationRules:data.automationRules,noteTemplates:data.noteTemplates,scoreWeights:data.scoreWeights,inactivityThresholds:data.inactivityThresholds}))} onReactivate={id=>setState(current=>({...current,clients:current.clients.map(client=>String(client.client_id||client.id)===String(id)?{...client,prospectingExcluded:false,prospectingOpposition:false,engagementBlocked:false,exclusionReason:"",commercialStatus:"À qualifier"}:client)}))}/>);
  return (
    <div className="app">
      <main>
        {headerActionsTarget ? createPortal(headerActions, headerActionsTarget) : <header style={{ justifyContent: "flex-end" }}>{headerActions}</header>}
        <div className="topbar">
          <label className="search">
            <Search />
            <input
              aria-label="Recherche globale"
              placeholder="Établissement, SIRET, SIREN, ville, téléphone, contact…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        </div>

        {toast && (
          <div className="toast" role="status">
            {toast}
            <button aria-label="Fermer le message" onClick={() => setToast("")}>
              <X />
            </button>
          </div>
        )}
        {storageError && (
          <p className="error" role="alert">
            {onPersist
              ? "Enregistrement sur le serveur impossible : les modifications restent en mémoire et seront renvoyées à la prochaine action."
              : "Sauvegarde locale indisponible : les modifications restent en mémoire pendant cette session."}
          </p>
        )}
        {actionFeedback &&
          modal?.type !== "saved" &&
          !state.pendingResultClientId && (
            <section className="dailyFeedback" role="status">
              <div>
                <strong>{actionFeedback.label}</strong>
                <p>
                  {myActual.callResults} / {myGoals.callResults} résultats ·{" "}
                  {myActual.decisionMakersReached} décideurs joints ·{" "}
                  {myActual.needsDetected} / {myGoals.needsDetected} besoins
                  détectés
                  {myActual.needsDetected >= myGoals.needsDetected
                    ? " · Objectif besoins atteint"
                    : ""}
                </p>
              </div>
              {recommended && (
                <button
                  className="primary"
                  onClick={() => { setModal(null); setActionFeedback(null); openCall(recommended.client.id); }}
                >
                  Continuer ma prospection · {recommended.client.name} (
                  {recommended.score}/100)
                </button>
              )}
              <button
                className="textAction"
                aria-label="Fermer le retour d’action"
                onClick={() => setActionFeedback(null)}
              >
                <X />
              </button>
            </section>
          )}
        {(
          <nav className="commercialTabs" aria-label="Actions commerciales">
            {nav.map(([key, label, Icon]) => (
              <button
                key={key}
                className={tab === key ? "on" : ""}
                aria-current={tab === key ? "page" : undefined}
                onClick={() => navigate(key)}
              >
                <Icon />
                {label}
                {key !== "day" && key !== "manager" && key !== "follow" && (
                  <span aria-hidden="true">{navCounts[key]}</span>
                )}
              </button>
            ))}
          </nav>
        )}
        {tab === "clients" &&
          (clientOpen ? (
            <>
              <button
                className="secondary backButton"
                onClick={() => setClientOpen(false)}
              >
                Retour à la liste
              </button>
              {renderAssistant("client")}
              <ClientRecord key={p.id} {...recordProps(p)} />
            </>
          ) : (
            <Panel title={`Liste des établissements (${searched.length})`}>
              {searched.map((c) => (
                <button
                  className="row"
                  key={c.id}
                  onClick={() => {
                    selectClient(c.id);
                    setClientOpen(true);
                  }}
                >
                  <div>
                    <b>{c.name}</b>
                    <small>
                      {c.city} · {c.status} · {c.owner}
                    </small>
                    <small>SIRET {c.siret}</small>
                  </div>
                  <ChevronRight />
                </button>
              ))}
              {!searched.length && <Empty />}
            </Panel>
          ))}
        {tab === "day" && (
          <Dashboard
            renderActivity={content => renderAssistant("activity", content)}
            dayContent={dayContent}
            state={visibleState}
            today={today}
            isHyperadmin={canPilot}
            search={search}
            selectedId={p.id}
            onSelect={selectClient}
            onTask={openTask}
            userId={user.id}
            onGoal={() => setModal({ type: "goal", userId: user.id, name: user.name })}
            onOpenAction={openRecommended}
            onOpenClient={openCall}
            onOpenNeed={(id) => {
              if (navigate("needs")) setNeedFocus(id);
            }}
            onNavigate={navigate}
            onNewTask={() => setModal({ type: "new-task" })}
            onCompleteTask={(id) =>
              run(
                (s) => crm.completeTask(s, id),
                "Action terminée et journal mis à jour.",
              )
            }
          />
        )}
        {tab === "prospects" && (
          <>
            <div className="kpis progressStats">
              <K n={todayResults.length} l="Résultats d’appels saisis" />
              <K
                n={todayResults.filter((r) => r.deciderJoined).length}
                l="Décideurs joints"
              />
              <K
                n={
                  todayResults.filter((r) => r.label === "Nouveau besoin")
                    .length
                }
                l="Besoins détectés"
              />
              <K
                n={openTasks.filter((t) => t.category === "contact").length}
                l="Contacts à compléter / trouver"
              />
            </div>
            {session && (
              <div className="sessionProgress">
                <span>
                  {session.completed.length} / {session.ids.length} traités
                </span>
                <progress
                  value={session.completed.length}
                  max={session.ids.length || 1}
                />
                {paused && <span>En pause</span>}
              </div>
            )}
            <div className="prospectionGrid">
              <Panel title="File priorisée">
                <Select
                  label="Filtrer la file"
                  value={normalizeProspectingFilter(filter)}
                  onChange={(e) => {if(state.pendingResultClientId){setToast("Terminez l’appel en cours avant de changer le filtre.");return;}setFilter(e.target.value);}}
                >
                  {prospectingFilters.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </Select>
                {filter === "ambiguities" ? <FranceTravailRobot inlineAmbiguities api={franceTravailApi} onSync={rows => setState(current => mergeFranceTravailProspects(current, rows))} /> : <>
                <label className="check">
                  <input
                    type="checkbox"
                    checked={onlyMine}
                    onChange={(e) => {if(state.pendingResultClientId){setToast("Terminez l’appel en cours avant de changer le filtre.");return;}setOnlyMine(e.target.checked);}}
                  />
                  Voir uniquement mes établissements
                </label>
                <CommercialFavorites state={myState} client={hasVisibleSelection ? p : null} onChange={ids=>setState(current=>({...current,commercialFavorites:ids}))} onSelect={id=>openCall(id)} />
                <p className="hint" role="status">{visibleClients.length} établissement{visibleClients.length > 1 ? "s" : ""}</p>
                {visibleClients.map((c) => (
                    <button
                      key={c.id}
                      className={`row ${c.id === p.id ? "selected" : ""}`}
                      onClick={() => {
                        if (!paused) openCall(c.id);
                      }}
                      disabled={paused}
                    >
                      <Building2 />
                      <div>
                        <b>{c.name}</b>
                        <span className="callPurpose" data-purpose={callPurpose(c)}>{callPurpose(c)}</span>
                        <small>
                          {c.city} · {c.owner}
                        </small>
                        <span className="status">{sentenceCase(c.status)}</span>
                      </div>
                      <strong>{c.score}</strong>
                    </button>
                  ))}
                {!visibleClients.length && <Empty />}
                </>}
              </Panel>
              <div className="prospectionStickyColumn" style={{display: hasVisibleSelection ? undefined : "none"}}>{hasVisibleSelection && renderAssistant("client")}<ClientRecord key={p.id} {...recordProps(p)} compact /></div>
              <div className="prospectionStickyColumn" style={{display: hasVisibleSelection ? undefined : "none"}}>
                <Panel title="Résultat de mon appel">
                  <p className="callContext"><span className="callPurpose" data-purpose={callPurpose(p)}>{callPurpose(p)}</span> · {p.name}</p>
                  {state.pendingResultClientId === p.id && (
                    <div className="success">
                      ✓ Contact actualisé. Renseigne le résultat final du même
                      appel avant de passer au prospect suivant.
                    </div>
                  )}
                  <button
                    className="primary wide"
                    disabled={paused}
                    onClick={() => setModal({ type: "result-picker" })}
                  >
                    <PhoneCall />
                    Saisir le résultat de mon appel
                  </button>
                  <CallChoices
                    disabled={paused}
                    onResult={chooseResult}
                    onAction={(type) => setModal({ type, clientId: p.id })}
                  />
                  <TextArea
                    label="Note rapide"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={paused}
                  />
                </Panel>
              </div>
            </div>

          </>
        )}
        {tab === "follow" && (
          <Agenda
            state={visibleState}
            tasks={openTasks}
            onTask={openTask}
            onNewTask={() => setModal({ type: "new-task" })}
          />
        )}
        {tab === "needs" && (
          <>
            {needFocus && (
              <button
                className="secondary backButton"
                onClick={() => setNeedFocus(null)}
              >
                Voir tous les besoins
              </button>
            )}
            <Needs
              state={visibleState}
              focusId={needFocus}
              onNew={() => setModal({ type: "need", clientId: p.id })}
              onCreateQuote={(need) => setModal({ type: "quote", need })}
              onSend={(id) =>
                run(
                  (s) => crm.sendQuote(s, id),
                  "Devis déclaré envoyé. Relance créée automatiquement.",
                )
              }
              onSign={(id) =>
                run(
                  (s) => crm.signQuote(s, id),
                  "Signature enregistrée. Besoin transmis automatiquement dans les données locales.",
                )
              }
              onRefuse={(quote) => setModal({ type: "refuse", quote })}
              onStatus={(id, status) =>
                run((s) => crm.setNeedStatus(s, id, status))
              }
              onPayload={(id) => setModal({ type: "payload", needId: id })}
            />
          </>
        )}
        {tab === "react" && <Reactivation state={visibleState} onClient={openCall} />}
        {tab === "manager" && canPilot && (
          <Manager
            state={visibleState}
            onClient={openCall}
            onTask={openTask}
            onCalendar={() => setModal({ type: "calendar" })}
            onNeed={id => { if (navigate("needs")) setNeedFocus(id); }}
            onFinancialGoals={isHyperadmin ? data => commit(s => saveFinancialGoal(s, data, user)) : undefined}
            onWeeklyGoals={isHyperadmin ? (userId, week, goals, carry) => commit(s => setWeeklyGoalPlan(s, userId, week, goals, carry, user)) : undefined}
            onUsualWeek={(userId, from, weekdays) => commit(s => saveUsualWeek(s, userId, from, weekdays, user))}
            onEditGoals={isHyperadmin ? (userId, name) => setModal({ type: "goal", userId, name }) : undefined}
          />
        )}
      </main>
      {modal?.type === "quote" && <NeedQuotationBuilder need={modal.need} clientId={state.clients.find(client => client.id === modal.need.clientId)?.establishment_id || modal.need.clientId} onClose={close} onCreated={quotation => {
        commit(current => recordQuotationForNeed(current, modal.need, quotation));
        setToast(`Devis n° ${quotation.quotationNumber} créé et disponible côté admin.`);
      }} />}
      {modal?.type === "client-edit" && <ProspectingClientEditor clientId={modalClient.client_id || modalClient.establishment_id || modalClient.id} onClose={close} onUpdated={updated => {
        setState(current => {
          const saved = {...current,clients:current.clients.map(c=>c.id===modal.clientId?{...c,contacts:[]}:c)};
          const mapped = connectClients([updated],user,saved).clients.find(c=>String(c.client_id)===String(updated._id));
          return mapped ? {...current,clients:current.clients.map(c=>c.id===modal.clientId?{...mapped,id:c.id}:c)} : current;
        });
      }} />}
      {modal && !["quote", "client-edit"].includes(modal.type) && (
        <Modal
          title={
            modal.type === "call-correction" ? "Corriger le dernier appel" : modal.type === "invoice"
              ? "Facture impayée"
              : modal.type === "import"
              ? "Importer Excel / CSV"
              : modal.type === "result"
                ? modal.label
                : modal.type === "result-picker"
                  ? "Résultat de mon appel"
                  : modal.type === "saved"
                    ? "✓ Résultat enregistré"
                    : modal.type === "contact-choice"
                      ? "Contact parti"
                      : modal.type === "contact-form"
                        ? "Contact de l’établissement"
                        : modal.type === "quote"
                          ? "Créer une version du devis"
                          : modal.type === "refuse"
                            ? "Motif du refus du devis"
                            : modal.type === "payload"
                              ? "Informations transmises au recrutement"
                              : modal.type === "need"
                                ? "Nouveau besoin commercial"
                                : modal.type === "new-task"
                                  ? "Nouvelle tâche"
                                  : modal.type === "goal"
                                    ? `Mes objectifs · ${modal.name || user.name}`
                                    : modal.type === "owner"
                                      ? "Responsabilité commerciale"
                                      : modal.type === "brochure"
                                        ? "Plaquette commerciale"
                                        : modal.type === "engagement"
                                          ? "Engagement commercial"
                                          : modal.type === "calendar"
                                            ? "Calendrier agence"
                                            : "Traiter une action"
          }
          onClose={close}
        >
          {modal.type === "invoice" && (
            <>
              <h3>{modalClient.name} · {modal.task.invoiceReference}</h3>
              <p>Montant restant à régler : <b>{Number(modal.task.remainingAmount).toLocaleString("fr-FR")} €</b></p>
              <p>Échéance : {modal.task.invoiceDue ? formatDate(modal.task.invoiceDue) : "Non renseignée"}</p>
              <p>Statut : {modal.task.invoice.status || "Impayée"}</p>
              <p className="hint">Cette tâche reste visible jusqu’à la mise à jour du règlement dans les données de facturation.</p>
              <button className="primary" onClick={() => { close(); openCall(modalClient.id); }}>Contacter le client</button>
            </>
          )}
          {modal.type === "import" && (
            <ImportProspects
              state={visibleState}
              onApply={(next) => {
                setState(next);
                setToast(
                  "Import terminé : fiches enrichies, signaux et actions créés.",
                );
              }}
            />
          )}
          {modal.type === "result-picker" && (
            <>
              <p>
                Appel passé sur un téléphone externe. Choisis son résultat
                final.
              </p>
              <CallChoices
                onResult={chooseResult}
                onAction={(type) => setModal({ type, clientId: p.id })}
              />
            </>
          )}
          {modal.type === "call-correction" && <CorrectLastCall api={franceTravailApi} client={modalClient} options={crm.resultOptions} onCorrected={data=>{setState(current=>applyCallCorrectionResponse(current,data));setToast(data.skippedTasks?.length ? "Appel corrigé. Les tâches protégées non validées ont été conservées." : "Dernier appel corrigé.");}} onClose={close}/>}
          {modal.type === "result" && (
            <CallResultForm
              key={modal.label}
              label={modal.label}
              note={note}
              today={today}
              closures={state.calendar.closures}
              onSave={saveResult}
              saving={savingResult}
              automationRules={state.automationRules}
              noteTemplates={state.noteTemplates}
              clientId={modalClient?.establishment_id || modalClient?.id}
              hasQuote={state.quotes.some(q => String(q.clientId) === String(modalClient?.id))}
            />
          )}
          {modal.type === "contact-choice" && (
            <>
              <p>Avez-vous obtenu le nom de son remplaçant ?</p>
              <div className="contactChoices">
                <button
                  onClick={() =>
                    setModal({
                      type: "contact-form",
                      replace: true,
                      duringCall: true,
                      clientId: modalClient.id,
                    })
                  }
                >
                  Oui, j’ai le nouveau contact
                </button>
                <button
                  onClick={() =>
                    setModal({
                      type: "contact-form",
                      replace: true,
                      duringCall: true,
                      clientId: modalClient.id,
                    })
                  }
                >
                  Oui, informations incomplètes
                </button>
                <button
                  onClick={() => {
                    if (
                      run((s) =>
                        crm.contactDepartedUnknown(s, modalClient.id, note),
                      )
                    ) {
                      close();
                      setTab("prospects");
                      setToast(
                        "Recherche du remplaçant programmée au prochain jour ouvré. Renseigne le résultat final du même appel.",
                      );
                    }
                  }}
                >
                  Non · créer « Trouver le nouveau contact » puis continuer le
                  même appel
                </button>
              </div>
            </>
          )}
          {modal.type === "contact-form" && (
            <ContactForm
              contact={
                modal.contact ||
                modalClient.contacts.find((c) => c.id === modal.contactId)
              }
              replace={modal.replace}
              duringCall={modal.duringCall}
              onSave={(data) => {
                commit((s) =>
                  modal.contactId
                    ? crm.updateContact(
                        s,
                        modalClient.id,
                        modal.contactId,
                        data,
                      )
                    : crm.addContact(s, modalClient.id, data, {
                        replace: modal.replace,
                        duringCall: modal.duringCall,
                      }),
                );
                close();
                setToast(
                  `✓ Contact actualisé : ${data.firstName || ""} ${data.lastName}${modal.duringCall ? " · renseigne le résultat final du même appel" : ""}`,
                );
              }}
            />
          )}
          {modal.type === "need" && (
            <Form
              onSubmit={(fd) => {
                commit((s) =>
                  crm.createNeed(s, modalClient.id, {
                    ...Object.fromEntries(fd),
                    qty: Number(fd.get("qty")),
                  }),
                );
                close();
                if (navigate("needs")) setNeedFocus(null);
              }}
            >
              <p>{modalClient.name}</p>
              <NeedFields clientId={modalClient?.establishment_id || modalClient?.id} hasQuote={state.quotes.some(q => String(q.clientId) === String(modalClient?.id))} />
              <button className="primary wide">
                Créer le besoin commercial
              </button>
            </Form>
          )}

          {modal.type === "refuse" && (
            <Form
              onSubmit={(fd) => {
                commit((s) =>
                  crm.refuseQuote(
                    s,
                    modal.quote.id,
                    fd.get("reason"),
                    fd.get("notes"),
                  ),
                );
                close();
              }}
            >
              <Select label="Motif obligatoire" name="reason" required>
                <option value="">Choisir</option>
                {crm.lossReasons.map((r) => (
                  <option key={r}>{r}</option>
                ))}
              </Select>
              <TextArea label="Commentaires" name="notes" />
              <button className="primary wide">Enregistrer le refus</button>
            </Form>
          )}
          {modal.type === "payload" && (
            <>
              <p className="hint">
                Copie locale des données pour l’intégration future au système
                recrutement.
              </p>
              <pre className="payloadView">
                {JSON.stringify(
                  state.recruitmentNeeds.find(
                    (n) => n.needId === modal.needId,
                  ) || {
                    message:
                      "Trace historique conservée ; payload détaillé non disponible dans les données de la version précédente.",
                  },
                  null,
                  2,
                )}
              </pre>
            </>
          )}
          {modal.type === "saved" && (
            <>
              <div className="success">
                {p.name} · {modal.label}
                <br />
                Fiche, historique et prochaine action mis à jour.
              </div>
              <p>
                {myActual.callResults} / {myGoals.callResults} résultats ·{" "}
                {myActual.decisionMakersReached} décideurs joints ·{" "}
                {myActual.needsDetected} / {myGoals.needsDetected} besoins
                détectés
                {myActual.needsDetected >= myGoals.needsDetected
                  ? " · ✓ Objectif besoins atteint"
                  : ""}
              </p>
              <button className="primary wide" onClick={nextProspect}>
                Continuer ma prospection <ChevronRight />
              </button>
              <button
                className="secondary wide"
                onClick={() => {
                  close();
                  if (navigate("clients")) setClientOpen(true);
                }}
              >
                Voir la fiche client
              </button>
            </>
          )}
          {modal.type === "new-task" && (
            <TaskForm
              state={visibleState}
              clientId={p.id}
              onSave={(id, data) => {
                commit((s) =>
                  crm.scheduleWorkingDayTask(
                    s,
                    id,
                    data.title,
                    data.due,
                    data.category,
                    { notes: data.notes, time: data.time },
                  ),
                );
                close();
              }}
            />
          )}
          {modal.type === "goal" && (isHyperadmin || (modal.userId || user.id) === user.id) && (
            <Form
              onSubmit={(fd) => {
                if (!isHyperadmin && (modal.userId || user.id) !== user.id) throw new Error("Vous pouvez modifier uniquement vos propres objectifs.");
                const goals = {
                  callResults: Number(fd.get("goal")),
                  decisionMakersReached: Number(
                    fd.get("decisionMakersReached"),
                  ),
                  needsDetected: Number(fd.get("needsDetected")),
                  quotesSent: Number(fd.get("quotesSent")),
                };
                commit(s => setDailyGoals(s, modal.userId || user.id, goals));
                close();
              }}
            >
              <Field
                label="Nombre de résultats d’appel par jour"
                name="goal"
                type="number"
                min="1"
                max="500"
                step="1"
                defaultValue={getDailyGoals(state, modal.userId || user.id).callResults}
                required
              />
              {Object.entries(goalLabels)
                .filter(([key]) => key !== "callResults")
                .map(([key, label]) => (
                  <Field
                    key={key}
                    label={label}
                    name={key}
                    type="number"
                    min="1"
                    max="500"
                    step="1"
                    defaultValue={getDailyGoals(state, modal.userId || user.id)[key]}
                    required
                  />
                ))}
              <button className="primary wide">Enregistrer</button>
            </Form>
          )}
          {modal.type === "owner" && (
            <Form
              onSubmit={(fd) => {
                const data = Object.fromEntries(fd);
                commit((s) =>
                  crm.logCommercialEvent(
                    {
                      ...s,
                      clients: s.clients.map((c) =>
                        c.id === modalClient.id ? { ...c, ...data } : c,
                      ),
                      tasks: s.tasks.map((t) =>
                        t.clientId === modalClient.id && !t.done
                          ? { ...t, ownerId: data.ownerId, agency: data.agency }
                          : t,
                      ),
                    },
                    modalClient.id,
                    "ownership",
                    "Responsabilité commerciale actualisée",
                  ),
                );
                close();
              }}
            >
              <Field
                label="Agence propriétaire"
                name="agency"
                defaultValue={modalClient.agency}
                required
              />
              <Field
                label="Commercial responsable"
                name="owner"
                defaultValue={modalClient.owner}
                required
              />
              <Field
                label="Identifiant du commercial"
                name="ownerId"
                defaultValue={modalClient.ownerId}
                required
              />
              <Field
                label="Commercial secondaire"
                name="secondaryOwner"
                defaultValue={modalClient.secondaryOwner}
              />
              <button className="primary wide">
                Enregistrer la responsabilité
              </button>
            </Form>
          )}

          {modal.type === "brochure" && (
            <Form
              onSubmit={(fd) => {
                commit((s) =>
                  crm.markBrochureSent(s, modalClient.id, [fd.get("brochureReason"), fd.get("otherReason"), fd.get("notes")].filter(Boolean).join(" · ")),
                );
                close();
                setToast(
                  "Plaquette déclarée envoyée. Relance programmée dans 3 jours ouvrés.",
                );
              }}
            >
              <p>
                Présentation → plaquette envoyée → relance automatique →
                qualification.
              </p>
              <p className="hint">
                Envoie la plaquette via ta messagerie, puis déclare l’envoi ici.
              </p>
              {contact?.email && (
                <a
                  className="secondary"
                  href={`mailto:${contact.email}?subject=Présentation%20JM%20INTERIM`}
                >
                  Préparer un email
                </a>
              )}
              <MotifSelect label="Motif de l’envoi" name="brochureReason" options={["Présentation JM INTERIM", "Demande du client", "Information sur nos services", "Autre"]} />
              <TextArea label="Commentaire" name="notes" />
              <button className="primary wide">
                Déclarer la plaquette envoyée et créer la relance
              </button>
            </Form>
          )}
          {modal.type === "calendar" && (
            <Form
              onSubmit={(fd) => {
                const closures = String(fd.get("closures") || "")
                  .split(/\s+/)
                  .filter(Boolean);
                if (closures.some((d) => !dateValue(d)))
                  throw new Error("Utilise une date AAAA-MM-JJ par ligne.");
                commit((s) =>
                  crm.rolloverTasks({
                    ...s,
                    calendar: { ...s.calendar, closures },
                    tasks: s.tasks.map((t) =>
                      t.done
                        ? t
                        : {
                            ...t,
                            scheduled_date: addBusinessDays(
                              t.due_date_original,
                              0,
                              closures,
                            ),
                          },
                    ),
                  }),
                );
                close();
              }}
            >
              <p>
                Week-ends et jours fériés nationaux sont exclus automatiquement.
                Ajoute ici les fermetures de l’agence.
              </p>
              <TextArea
                label="Fermetures (une date AAAA-MM-JJ par ligne)"
                name="closures"
                defaultValue={state.calendar.closures.join("\n")}
              />
              <button className="primary wide">
                Enregistrer le calendrier
              </button>
            </Form>
          )}
          {modal.type === "task-detail" && (
            <>
              <p>
                <b>{modal.task.title}</b>
              </p>
              <p>
                {state.clients.find((c) => c.id === modal.task.clientId)?.name}
                <br />
                Échéance initiale : {formatDate(modal.task.due_date_original)}
              </p>
              <p>{modal.task.notes}</p>
              <button
                className="primary wide"
                onClick={() => {
                  if (
                    run(
                      (s) => crm.completeTask(s, modal.task.id),
                      "Action terminée.",
                    )
                  )
                    close();
                }}
              >
                Déclarer l’action réalisée
              </button>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
